import React, { useState, useEffect, useCallback } from "react";
import { GridToolbar } from "@mui/x-data-grid-pro";
import { Button, Snackbar, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import InventoryItemDialog from "./dialogs/InventoryItemDialog";
import AdjustInventoryDialog from "./dialogs/AdjustInventoryDialog";
import ThemedDataGridPro from "./ThemedDataGridPro";
import ThemedIconButton from "./ThemedIconButton";
import ThemedTypography from "./ThemedTypography";
import ThemedTabs from "./ThemedTabs";
import { decoratedFetch } from "../req_utils";
import { GENESIS_LOGO_COLOR } from "../constants";
import { useSelector } from "react-redux";

const TRANSACTION_TYPE_LABELS = {
  stock_in: "Stock In",
  manual_adjustment: "Adjustment",
  project_deduction: "Project Deduction",
  reversal: "Returned to Stock",
};

const formatTimestamp = (value) =>
  value ? new Date(value).toLocaleString() : "";

const gridFocusOutlineSx = {
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
    {
      outline: "none",
    },
};

const InventoryTab = ({ value, index }) => {
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryItemsLoading, setInventoryItemsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editableItem, setEditableItem] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [adjustableItem, setAdjustableItem] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [allocations, setAllocations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const listInventoryItems = useCallback(() => {
    if (!employee_branch_id) return;
    setInventoryItemsLoading(true);
    decoratedFetch(`/list_inventory_items?branch_id=${employee_branch_id}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setInventoryItems(data);
        setInventoryItemsLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching inventory items: ${error}`);
        setInventoryItems([]);
        setInventoryItemsLoading(false);
      });
  }, [employee_branch_id]);

  useEffect(() => {
    if (value !== index) {
      return;
    }
    listInventoryItems();
  }, [value, index, listInventoryItems]);

  useEffect(() => {
    if (!selectedItem) {
      setAllocations([]);
      setTransactions([]);
      return;
    }
    setDetailLoading(true);
    Promise.all([
      decoratedFetch(
        `/list_inventory_allocations?inventory_item_id=${selectedItem.id}`,
      ).then((response) => response.json()),
      decoratedFetch(
        `/list_inventory_transactions?inventory_item_id=${selectedItem.id}`,
      ).then((response) => response.json()),
    ])
      .then(([allocationData, transactionData]) => {
        setAllocations(allocationData);
        setTransactions(transactionData);
        setDetailLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching inventory detail: ${error}`);
        setAllocations([]);
        setTransactions([]);
        setDetailLoading(false);
      });
  }, [selectedItem]);

  const refresh = () => {
    listInventoryItems();
    if (selectedItem) {
      setSelectedItem({ ...selectedItem });
    }
  };

  const handleItemDialogClose = () => {
    setItemDialogOpen(false);
    setEditableItem(null);
    setSelectedItem(null);
    listInventoryItems();
  };

  const handleAdjustDialogClose = () => {
    setAdjustableItem(null);
    refresh();
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 3 },
    { field: "material_type", headerName: "Type", flex: 2 },
    { field: "material_vendor", headerName: "Manufacturer", flex: 2 },
    { field: "material_style", headerName: "Product", flex: 2 },
    { field: "material_color", headerName: "Color", flex: 2 },
    {
      field: "quantity",
      headerName: "On Hand",
      flex: 1.5,
      renderCell: (params) => {
        const quantity = parseFloat(params.value) || 0;
        const threshold = parseFloat(params.row.reorder_threshold);
        if (quantity < 0) {
          return <Chip size="small" color="error" label={quantity} />;
        }
        if (!isNaN(threshold) && quantity <= threshold) {
          return <Chip size="small" color="warning" label={quantity} />;
        }
        return quantity;
      },
    },
    {
      field: "size_unit",
      headerName: "Unit",
      flex: 1,
      renderCell: (params) => params.value?.name || "",
    },
    { field: "allocated_quantity", headerName: "Allocated", flex: 1.5 },
    { field: "reorder_threshold", headerName: "Reorder At", flex: 1.5 },
    { field: "location", headerName: "Location", flex: 2 },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      flex: 1.5,
      renderCell: (params) => (
        <div>
          <Tooltip title="Adjust stock">
            <span>
              <ThemedIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setAdjustableItem(params.row);
                }}
              >
                <SwapVertIcon fontSize="small" />
              </ThemedIconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <span>
              <ThemedIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setEditableItem(params.row);
                  setItemDialogOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </ThemedIconButton>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  const allocationColumns = [
    {
      field: "project_custom_id",
      headerName: "Project ID",
      flex: 2,
    },
    { field: "project_name", headerName: "Project", flex: 3 },
    { field: "project_status", headerName: "Status", flex: 2 },
    { field: "allocated_quantity", headerName: "Quantity", flex: 1.5 },
    {
      field: "created_time",
      headerName: "Allocated On",
      flex: 2,
      renderCell: (params) => formatTimestamp(params.value),
    },
  ];

  const transactionColumns = [
    {
      field: "created_time",
      headerName: "When",
      flex: 2,
      renderCell: (params) => formatTimestamp(params.value),
    },
    {
      field: "transaction_type",
      headerName: "Type",
      flex: 2,
      renderCell: (params) =>
        TRANSACTION_TYPE_LABELS[params.value] || params.value,
    },
    { field: "quantity_delta", headerName: "Change", flex: 1.5 },
    { field: "quantity_after", headerName: "On Hand After", flex: 1.5 },
    { field: "project_custom_id", headerName: "Project", flex: 2 },
    { field: "created_by", headerName: "By", flex: 2 },
    { field: "notes", headerName: "Note", flex: 3 },
  ];

  return (
    <div>
      <Snackbar
        sx={{
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingBottom: 10,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          style={{ margin: 5 }}
          onClick={() => {
            setEditableItem(null);
            setItemDialogOpen(true);
          }}
        >
          Create Inventory Item
        </Button>
      </div>
      <div style={{ height: "auto", width: "100%" }}>
        <ThemedDataGridPro
          rows={inventoryItems}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          loading={inventoryItemsLoading}
          onRowClick={(params) => setSelectedItem(params.row)}
          localeText={{ noRowsLabel: "No inventory items" }}
          sx={gridFocusOutlineSx}
        />
      </div>
      {selectedItem && (
        <Box sx={{ marginTop: "24px" }}>
          <ThemedTypography variant="h6" sx={{ fontWeight: "bold" }}>
            {selectedItem.name}
          </ThemedTypography>
          <ThemedTypography variant="body2" sx={{ marginBottom: "10px" }}>
            Pending shows stock already deducted at PO Received but likely still
            on the shelf, which is why a physical count can be higher than On
            Hand.
          </ThemedTypography>
          <ThemedTabs
            value={detailTab}
            labels={["Pending", "History"]}
            onChange={(event, newValue) => setDetailTab(newValue)}
          />
          <Box sx={{ marginTop: "10px" }}>
            {detailTab === 0 ? (
              <ThemedDataGridPro
                rows={allocations}
                columns={allocationColumns}
                loading={detailLoading}
                localeText={{
                  noRowsLabel: "Nothing allocated from this item",
                }}
                sx={gridFocusOutlineSx}
              />
            ) : (
              <ThemedDataGridPro
                rows={transactions}
                columns={transactionColumns}
                loading={detailLoading}
                localeText={{ noRowsLabel: "No stock movements yet" }}
                sx={gridFocusOutlineSx}
              />
            )}
          </Box>
        </Box>
      )}
      <InventoryItemDialog
        isOpen={itemDialogOpen}
        inventoryItem={editableItem}
        handleClose={handleItemDialogClose}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
      />
      <AdjustInventoryDialog
        isOpen={!!adjustableItem}
        inventoryItem={adjustableItem}
        handleClose={handleAdjustDialogClose}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
      />
    </div>
  );
};

export default InventoryTab;
